import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GridStatePersistence } from './GridStatePersistence';
import type { FilterColumn } from './FilterColumn';
import type { SortColumn } from './SortColumn';
import type { GridStatePersistenceContract } from './GridStatePersistenceContract';
import type { PersistedGridState } from './PersistedGridState';
import type { ObservableTableState, ReactTableFilter, ReactTableSortBy } from './ObservableTableState';
import type { UseUserStatePersistenceOptions, PersistableFormInstance } from './UseUserStatePersistenceOptions';
import type { UseUserStatePersistenceResult } from './UseUserStatePersistenceResult';

// ── Helpers ────────────────────────────────────────────────────────────

const noopHandler = () => {};

/** Map react-table `{ id, value }` filters → `FilterColumn`. */
function mapFilters(
  filters: ReactTableFilter[],
  globalFilter: unknown,
): FilterColumn[] {
  const mapped: FilterColumn[] = filters.map((filter) => ({
    columnId: filter.id,
    filterValue: filter.value,
  }));

  if (
    globalFilter !== undefined &&
    globalFilter !== null &&
    globalFilter !== ''
  ) {
    mapped.push({ columnId: '__global__', filterValue: globalFilter });
  }

  return mapped;
}

/** Map react-table `{ id, desc }` sorts → `SortColumn`. */
function mapSorts(sortBy: ReactTableSortBy[]): SortColumn[] {
  return sortBy.map((sort) => ({
    columnId: sort.id,
    descending: sort.desc,
  }));
}

/** Stable JSON key for `useEffect` dependencies. */
function toStableKey(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

// ── Restore helper ─────────────────────────────────────────────────────

/**
 * Apply a `PersistedGridState` back onto the live `tableState` (and
 * optionally the `form`) by calling the appropriate setter methods.
 */
function restoreGridState(
  tableState: ObservableTableState,
  saved: PersistedGridState,
  form?: PersistableFormInstance,
): void {
  // ── Filters ──────────────────────────────────────────────────────────
  const columnFilters = saved.filterColumns.filter(
    (filter) => filter.columnId !== '__global__',
  );
  const globalEntry = saved.filterColumns.find(
    (filter) => filter.columnId === '__global__',
  );

  if (columnFilters.length > 0) {
    tableState.filter.setAllFilters(
      columnFilters.map((filter) => ({
        id: filter.columnId,
        value: filter.filterValue,
      })),
    );
  }

  if (globalEntry) {
    tableState.filter.setGlobalFilter(globalEntry.filterValue);
  }

  // ── Sorts ────────────────────────────────────────────────────────────
  if (saved.sortColumns.length > 0) {
    tableState.columnMgmt.setSortBy(
      saved.sortColumns.map((sort) => ({
        id: sort.columnId,
        desc: sort.descending,
      })),
    );
  }

  // ── Paging ───────────────────────────────────────────────────────────
  // PersistedGridState.page is 1-based; react-table is 0-based.
    tableState.pagination.setPageSize(saved.pageSize);

  // ── Column visibility ────────────────────────────────────────────────
  if (saved.hiddenColumns && saved.hiddenColumns.length > 0) {
    tableState.columnMgmt.setHiddenColumns(saved.hiddenColumns);
  } else if (saved.hiddenColumns && saved.hiddenColumns.length === 0) {
    // Explicitly restore "all columns visible".
    tableState.columnMgmt.setHiddenColumns([]);
  }

  // ── Column order ─────────────────────────────────────────────────────
  if (saved.columnOrder && saved.columnOrder.length > 0) {
    tableState.columnMgmt.setColumnOrder(saved.columnOrder);
  }

  // ── Form values (opt-in) ──────────────────────────────────────────
  if (form && saved.formValues) {
    for (const [field, value] of Object.entries(saved.formValues)) {
      form.setValue(field, value, { shouldDirty: false });
    }
  }
}

// ── Hook ───────────────────────────────────────────────────────────────

/**
 * React hook that conditionally persists user state — grid filters, sort,
 * paging, column visibility / order and, optionally, form field values —
 * to `localStorage` and restores everything on page reload.
 *
 * Pass a `form` instance to opt-in to form-value persistence; omit it to
 * keep the hook grid-only (backward-compatible).
 *
 * @example
 * ```tsx
 * const form  = useForm({ defaultValues: { name: '' } });
 * const tableState = useDataTable({ ... });
 *
 * const { clearPreference } = useUserStatePersistence({
 *   enabled: true,
 *   storageName: 'employee-directory-grid',
 *   tableState,
 *   form,           // ← opt-in: form values are now persisted too
 * });
 * ```
 */
export function useUserStatePersistence(
  options: UseUserStatePersistenceOptions,
): UseUserStatePersistenceResult {
  const { enabled, clientId, storageName, tableState, originalHandlers = {}, form, onClearPreference, onRestoreState, onNoSavedState, debug = false } = options;

  // Build the final localStorage key, appending clientId when present.
  const resolvedStorageKey = useMemo(
    () =>
      clientId !== null && clientId !== undefined
        ? `${storageName}-${clientId}`
        : storageName,
    [storageName, clientId],
  );

  // ── Build / memoize the persistence instance ─────────────────────────
  const persistence = useMemo<GridStatePersistenceContract | null>(() => {
    if (!enabled) return null;
    return new GridStatePersistence(resolvedStorageKey, originalHandlers, debug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    resolvedStorageKey,
    originalHandlers.onFilter,
    originalHandlers.onSort,
    originalHandlers.onPaging,
    debug,
  ]);

  const onFilter =
    persistence?.onFilter ?? originalHandlers.onFilter ?? noopHandler;
  const onSort =
    persistence?.onSort ?? originalHandlers.onSort ?? noopHandler;
  const onPaging =
    persistence?.onPaging ?? originalHandlers.onPaging ?? noopHandler;

  // ── Restore state from localStorage on mount ─────────────────────────
  const restoredStateRef = useRef<PersistedGridState | null>(null);
  const hasRestoredRef = useRef(false);

  useEffect(() => {
    if (!enabled || !tableState || hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    const saved = GridStatePersistence.load(resolvedStorageKey);

    if (saved) {
      restoredStateRef.current = saved;

      if (debug) {
        console.log('[useUserStatePersistence] Restoring saved state', {
          storageName: resolvedStorageKey,
          saved,
        });
      }

      restoreGridState(tableState, saved, form);
      onRestoreState?.();
    } else {
      if (debug) {
        console.log('[useUserStatePersistence] No saved state found');
      }
      onNoSavedState?.();
    }
    // Only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, resolvedStorageKey, !!tableState]);

  // ── Stable keys for the table state slices ───────────────────────────
  const filtersKey = toStableKey(tableState?.state.filters);
  const globalFilterKey = toStableKey(tableState?.state.globalFilter);
  const sortByKey = toStableKey(tableState?.state.sortBy);
  const pageSize = tableState?.state.pageSize;
  const hiddenColumnsKey = toStableKey(tableState?.state.hiddenColumns);
  const columnOrderKey = toStableKey(tableState?.columnMgmt.columnOrder);

  // Refs to skip the initial mount for each watcher.
  const isFilterMounted = useRef(false);
  const isSortMounted = useRef(false);
  const isPagingMounted = useRef(false);
  const isColumnVisibilityMounted = useRef(false);
  const isColumnOrderMounted = useRef(false);

  // Track previous enabled value to detect disable transitions.
  const prevEnabledRef = useRef(enabled);

  // Latest handler refs.
  const onFilterRef = useRef(onFilter);
  onFilterRef.current = onFilter;
  const onSortRef = useRef(onSort);
  onSortRef.current = onSort;
  const onPagingRef = useRef(onPaging);
  onPagingRef.current = onPaging;

  // Keep a stable ref to the form so watcher effects can read it.
  const formRef = useRef(form);
  formRef.current = form;

  /**
   * After a grid-state persist, patch `formValues` into the same
   * localStorage entry when a form instance is provided.
   *
   * If no entry exists yet (e.g. no grid-state change has fired),
   * a new entry is created from the current grid state so that form
   * values are never silently dropped.
   */
  const patchFormValues = useCallback(() => {
    if (!formRef.current) return;
    if (typeof window === 'undefined') return;

    try {
      const raw = localStorage.getItem(resolvedStorageKey);
      let persisted: PersistedGridState;

      if (raw) {
        persisted = JSON.parse(raw) as PersistedGridState;
      } else if (tableState) {
        // No entry yet — seed one from the current grid state.
        persisted = {
          filterColumns: mapFilters(
            tableState.state.filters,
            tableState.state.globalFilter,
          ),
          sortColumns: mapSorts(tableState.state.sortBy),
          pageSize: tableState.state.pageSize,
          hiddenColumns: tableState.state.hiddenColumns ?? [],
          columnOrder: tableState.columnMgmt.columnOrder ?? [],
        };
      } else {
        return;
      }

      persisted.formValues = formRef.current.getValues() as Record<string, unknown>;
      localStorage.setItem(resolvedStorageKey, JSON.stringify(persisted));

      if (debug) {
        console.log('[useUserStatePersistence] Form values persisted', {
          storageName: resolvedStorageKey,
          formValues: persisted.formValues,
        });
      }
    } catch {
      // swallow — localStorage may be unavailable
    }
  }, [resolvedStorageKey, tableState, debug]);

  // ── Watch filters (column filters + global filter) ───────────────────
  useEffect(() => {
    if (!enabled || !tableState) return;
    if (!isFilterMounted.current) {
      isFilterMounted.current = true;
      return;
    }

    const filterColumns = mapFilters(
      tableState.state.filters,
      tableState.state.globalFilter,
    );
    const sortColumns = mapSorts(tableState.state.sortBy);

    onFilterRef.current(
      filterColumns,
      sortColumns,
      tableState.state.pageSize,
      tableState.state.hiddenColumns ?? [],
      tableState.columnMgmt.columnOrder ?? [],
    );
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filtersKey, globalFilterKey]);

  // ── Watch sort ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !tableState) return;
    if (!isSortMounted.current) {
      isSortMounted.current = true;
      return;
    }

    const filterColumns = mapFilters(
      tableState.state.filters,
      tableState.state.globalFilter,
    );
    const sortColumns = mapSorts(tableState.state.sortBy);

    onSortRef.current(
      filterColumns,
      sortColumns,
      tableState.state.pageSize,
      tableState.state.hiddenColumns ?? [],
      tableState.columnMgmt.columnOrder ?? [],
    );
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sortByKey]);

  // ── Watch paging ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !tableState) return;
    if (!isPagingMounted.current) {
      isPagingMounted.current = true;
      return;
    }

    const filterColumns = mapFilters(
      tableState.state.filters,
      tableState.state.globalFilter,
    );
    const sortColumns = mapSorts(tableState.state.sortBy);

    onPagingRef.current(
      filterColumns,
      sortColumns,
      tableState.state.pageSize,
      tableState.state.hiddenColumns ?? [],
      tableState.columnMgmt.columnOrder ?? [],
    );
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pageSize]);

  // ── Watch column visibility ──────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !tableState) return;
    if (!isColumnVisibilityMounted.current) {
      isColumnVisibilityMounted.current = true;
      return;
    }

    const filterColumns = mapFilters(
      tableState.state.filters,
      tableState.state.globalFilter,
    );
    const sortColumns = mapSorts(tableState.state.sortBy);

    // Persist via onFilter handler (captures full state including hiddenColumns).
    onFilterRef.current(
      filterColumns,
      sortColumns,
      tableState.state.pageSize,
      tableState.state.hiddenColumns ?? [],
      tableState.columnMgmt.columnOrder ?? [],
    );
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, hiddenColumnsKey]);

  // ── Watch column order ───────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !tableState) return;
    if (!isColumnOrderMounted.current) {
      isColumnOrderMounted.current = true;
      return;
    }

    const filterColumns = mapFilters(
      tableState.state.filters,
      tableState.state.globalFilter,
    );
    const sortColumns = mapSorts(tableState.state.sortBy);

    // Persist via onFilter handler (captures full state including columnOrder).
    onFilterRef.current(
      filterColumns,
      sortColumns,
      tableState.state.pageSize,
      tableState.state.hiddenColumns ?? [],
      tableState.columnMgmt.columnOrder ?? [],
    );
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, columnOrderKey]);

  // ── Watch form values (opt-in) ───────────────────────────────────────
  const formValues = form?.watch();
  const formValuesKey = toStableKey(formValues);
  const isFormMounted = useRef(false);

  useEffect(() => {
    if (!enabled || !form) return;
    if (!isFormMounted.current) {
      isFormMounted.current = true;
      return;
    }

    // Patch the latest form values into the persisted entry.
    patchFormValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formValuesKey]);

  // Reset mount flags when disabled so re-enabling skips the first event.
  // Also auto-clear preference when toggling from enabled → disabled.
  useEffect(() => {
    if (!enabled) {
      // If previously enabled, clear the persisted preference automatically.
      if (prevEnabledRef.current) {
        GridStatePersistence.clear(resolvedStorageKey);
        restoredStateRef.current = null;

        if (tableState) {
          tableState.filter.setAllFilters([]);
          tableState.filter.setGlobalFilter(undefined);
          tableState.columnMgmt.setSortBy([]);
          tableState.columnMgmt.setHiddenColumns([]);
          tableState.columnMgmt.setColumnOrder([]);
          tableState.pagination.setPageSize(tableState.state.pageSize);
        }

        if (debug) {
          console.log(
            '[UserStatePersistence] Persistence disabled — preference cleared and state reset',
            { storageName: resolvedStorageKey },
          );
        }
      }

      isFilterMounted.current = false;
      isSortMounted.current = false;
      isPagingMounted.current = false;
      isColumnVisibilityMounted.current = false;
      isColumnOrderMounted.current = false;
      isFormMounted.current = false;
      hasRestoredRef.current = false;
    }
    prevEnabledRef.current = enabled;
  }, [enabled, resolvedStorageKey, tableState]);

  // ── Clear preference callback ────────────────────────────────────────
  const clearPreference = useCallback(() => {
    GridStatePersistence.clear(resolvedStorageKey);

    // Reset the grid back to its default state.
    if (tableState) {
      tableState.filter.setAllFilters([]);
      tableState.filter.setGlobalFilter(undefined);
      tableState.columnMgmt.setSortBy([]);
      tableState.columnMgmt.setHiddenColumns([]);
      tableState.columnMgmt.setColumnOrder([]);
    }

    // Reset form field values back to empty strings.
    if (form) {
      const currentValues = form.getValues() as Record<string, unknown>;
      for (const field of Object.keys(currentValues)) {
        form.setValue(field, '', { shouldDirty: false });
      }
    }

    restoredStateRef.current = null;

    if (debug) {
      console.log('[UserStatePersistence] Preference cleared and state reset', {
        storageName: resolvedStorageKey,
      });
    }

    onClearPreference?.();
  }, [resolvedStorageKey, tableState, form, onClearPreference]);

  // ── Reset form values only ───────────────────────────────────────────
  const resetFormValues = useCallback(() => {
    if (form) {
      const currentValues = form.getValues() as Record<string, unknown>;
      for (const field of Object.keys(currentValues)) {
        form.setValue(field, '', { shouldDirty: false });
      }
    }

    onClearPreference?.();
  }, [form, onClearPreference]);

  return {
    onFilter,
    onSort,
    onPaging,
    clearPreference,
    resetFormValues,
    persistence,
    restoredState: restoredStateRef.current,
  };
}

/**
 * @deprecated Renamed to `useUserStatePersistence`.  This alias is
 * kept for backward compatibility and will be removed in a future
 * release.
 */
export const useGridStatePersistence = useUserStatePersistence;
